import { getAuthToken } from '../services/auth/tokenStorage';
import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  TextInput,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
  Alert,
  Keyboard,
  TouchableWithoutFeedback,
} from 'react-native';
import { ms, s } from '../utils/responsive';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';

import { createLogger } from '../utils/logger';
const logger = createLogger('FeedbackModal');

interface FeedbackModalProps {
  visible: boolean;
  onClose: () => void;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ visible, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleClose = () => {
    setName('');
    setEmail('');
    setMessage('');
    setShowSuccess(false);
    onClose();
  };

  const validateEmail = (emailToCheck: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailToCheck);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      Alert.alert('Required Field', 'Please enter your name.');
      return;
    }
    if (!email.trim()) {
      Alert.alert('Required Field', 'Please enter your email address.');
      return;
    }
    if (!validateEmail(email.trim())) {
      Alert.alert('Invalid Email', 'Please enter a valid email address.');
      return;
    }
    if (!message.trim()) {
      Alert.alert('Required Field', 'Please enter your feedback message.');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          timestamp: new Date().toISOString(),
          platform: Platform.OS,
        }),
      });

      if (response.ok) {
        setShowSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        throw new Error('Backend submission failed');
      }
    } catch (error) {
      try {
        const existingFeedback = await AsyncStorage.getItem('pendingFeedback');
        const feedbackArray = existingFeedback ? JSON.parse(existingFeedback) : [];
        feedbackArray.push({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          timestamp: new Date().toISOString(),
          platform: Platform.OS,
        });
        await AsyncStorage.setItem('pendingFeedback', JSON.stringify(feedbackArray));
      } catch (storageError) {
        logger.error('Failed to store feedback locally:', storageError);
      }

      setShowSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={handleClose}
    >
      <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
        <View style={styles.modalOverlay}>
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={styles.keyboardAvoidingView}
          >
            <View style={styles.modalContainer}>
              {showSuccess ? (
                <View style={styles.successContainer}>
                  <Text style={styles.successIcon}>✓</Text>
                  <Text style={styles.successTitle}>Thank You!</Text>
                  <Text style={styles.successText}>Your feedback has been submitted.</Text>
                </View>
              ) : (
                <>
                  <View style={styles.modalHeader}>
                    <Text style={styles.modalTitle}>Send Feedback</Text>
                    <TouchableOpacity
                      style={styles.closeButton}
                      onPress={handleClose}
                    >
                      <Text style={styles.closeButtonText}>×</Text>
                    </TouchableOpacity>
                  </View>

                  <ScrollView
                    style={styles.modalContent}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                  >
                    <Text style={styles.modalDescription}>
                      We'd love to hear from you! Share your feedback, report issues, or suggest improvements.
                    </Text>

                    <View style={styles.inputGroup}>
                      <Text style={styles.inputLabel}>Name *</Text>
                      <TextInput
                        style={styles.input}
                        placeholder="Enter your name"
                        placeholderTextColor="#999"
                        value={name}
                        onChangeText={setName}
                        autoCapitalize="words"
                        editable={!isSubmitting}
                      />
                    </View>

                    <View style={styles.inputGroup}>
                      <Text style={styles.inputLabel}>Email *</Text>
                      <TextInput
                        style={styles.input}
                        placeholder="Enter your email"
                        placeholderTextColor="#999"
                        value={email}
                        onChangeText={setEmail}
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoCorrect={false}
                        editable={!isSubmitting}
                      />
                    </View>

                    <View style={styles.inputGroup}>
                      <Text style={styles.inputLabel}>Message *</Text>
                      <TextInput
                        style={[styles.input, styles.messageInput]}
                        placeholder="Tell us what's on your mind..."
                        placeholderTextColor="#999"
                        value={message}
                        onChangeText={setMessage}
                        multiline={true}
                        numberOfLines={5}
                        textAlignVertical="top"
                        editable={!isSubmitting}
                      />
                    </View>

                    <TouchableOpacity
                      style={[
                        styles.submitButton,
                        isSubmitting && styles.submitButtonDisabled,
                      ]}
                      onPress={handleSubmit}
                      disabled={isSubmitting}
                      activeOpacity={0.8}
                    >
                      {isSubmitting ? (
                        <ActivityIndicator color="#FFFFFF" size="small" />
                      ) : (
                        <Text style={styles.submitButtonText}>Submit Feedback</Text>
                      )}
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={styles.cancelButton}
                      onPress={handleClose}
                      disabled={isSubmitting}
                    >
                      <Text style={styles.cancelButtonText}>Cancel</Text>
                    </TouchableOpacity>
                  </ScrollView>
                </>
              )}
            </View>
          </KeyboardAvoidingView>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(20),
  },
  keyboardAvoidingView: {
    width: '100%',
    maxWidth: s(400),
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(20),
    maxHeight: '90%',
    width: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 10,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5EA',
  },
  modalTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#333',
  },
  closeButton: {
    width: s(32),
    height: s(32),
    borderRadius: ms(16),
    backgroundColor: '#F3F6F8',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: ms(24, 0.3),
    color: '#666',
    lineHeight: ms(26, 0.3),
  },
  modalContent: {
    padding: ms(20),
  },
  modalDescription: {
    fontSize: ms(14, 0.3),
    color: '#666',
    lineHeight: ms(20, 0.3),
    marginBottom: ms(20),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  inputLabel: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#333',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#F3F6F8',
    borderRadius: ms(12),
    paddingHorizontal: ms(16),
    paddingVertical: ms(14),
    fontSize: ms(16, 0.3),
    color: '#333',
    borderWidth: 1,
    borderColor: '#E1E5EA',
  },
  messageInput: {
    minHeight: s(120),
    paddingTop: ms(14),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(16),
    alignItems: 'center',
    marginTop: ms(10),
    shadowColor: '#29BFC0',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 5,
  },
  submitButtonDisabled: {
    backgroundColor: '#A0D9DA',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },
  cancelButton: {
    paddingVertical: ms(16),
    alignItems: 'center',
    marginTop: ms(8),
    marginBottom: ms(10),
  },
  cancelButtonText: {
    color: '#666',
    fontSize: ms(14, 0.3),
  },
  successContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
    paddingVertical: ms(60),
  },
  successIcon: {
    fontSize: ms(48, 0.3),
    color: '#29BFC0',
    marginBottom: ms(16),
    fontWeight: 'bold',
  },
  successTitle: {
    fontSize: ms(22, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(8),
  },
  successText: {
    fontSize: ms(14, 0.3),
    color: '#666',
    textAlign: 'center',
  },
});

export default FeedbackModal;
